import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Music2, Play, RotateCcw, Square } from "lucide-react";
import { apiClient } from "@/lib/auth";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function IntroMusicEditor({ slug, intro, onSaved }) {
  const [prompt, setPrompt] = useState(intro.music_prompt || "");
  const [busy, setBusy] = useState(null);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const stop = () => { try { ref.current?.pause(); } catch {} setPlaying(false); };
  useEffect(() => stop, []);

  const play = () => {
    if (playing) return stop();
    if (!intro.music_url) return;
    const a = new Audio(`${BASE}${intro.music_url}`);
    a.volume = 0.6;
    a.onended = () => setPlaying(false);
    ref.current = a;
    setPlaying(true);
    a.play().catch(() => setPlaying(false));
  };

  const run = async (kind) => {
    stop();
    setBusy(kind);
    try {
      const { data } = kind === "gen"
        ? await apiClient.post(`/admin/demos/${slug}/intro/music`, { prompt }, { timeout: 90000 })
        : await apiClient.delete(`/admin/demos/${slug}/intro/music`);
      onSaved(data);
      setPrompt(data.music_prompt);
      toast.success(kind === "gen" ? "New swell generated" : "Default swell restored");
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Music generation failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="pt-4 border-t border-white/10 space-y-2" data-testid="intro-music-editor">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500">
        <Music2 className="w-3.5 h-3.5" /> Music swell
        <button onClick={play} disabled={!intro.music_url} data-testid="intro-music-play"
          className={`ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${playing ? "text-emerald-300 bg-emerald-500/10" : "text-slate-300 hover:text-brand-cyan hover:bg-white/5"} disabled:opacity-40`}>
          {playing ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />} {playing ? "Stop" : "Play"}
        </button>
      </div>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} maxLength={450} disabled={!!busy}
        data-testid="intro-music-prompt"
        className="w-full rounded-lg bg-navy-950/70 border border-white/10 focus:border-brand-cyan/60 outline-none p-3 text-sm text-slate-100 resize-y" />
      <div className="flex items-center gap-2">
        <button onClick={() => run("reset")} disabled={!!busy} data-testid="intro-music-reset"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-amber-500/10 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40">
          {busy === "reset" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Default
        </button>
        <button onClick={() => run("gen")} disabled={!!busy || prompt.trim().length < 5} data-testid="intro-music-generate"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors disabled:opacity-40">
          {busy === "gen" && <Loader2 className="w-3 h-3 animate-spin" />} {busy === "gen" ? "Generating…" : "Generate swell"}
        </button>
      </div>
    </div>
  );
}
